import { useContext, useEffect, useState } from 'react';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import { useDocument } from '../hooks/useDocument';
import { NumericKpiPoint } from '@common/types/kpiTypes';

function isGlobalScoreAverage(value: number | [number, number]): value is [number, number] {
  return Array.isArray(value) && value.length === 2;
}

function isTransparencyScoreAverage(value: number | [number, number]): value is [number, number] {
  return Array.isArray(value) && value.length === 2;
}

function isInclusivityScoreAverage(value: number | [number, number]): value is [number, number] {
  return Array.isArray(value) && value.length === 2;
}

function isRationalScoreAverage(value: number | [number, number]): value is [number, number] {
  return Array.isArray(value) && value.length === 2;
}

function isHopefulScoreAverage(value: number | [number, number]): value is [number, number] {
  return Array.isArray(value) && value.length === 2;
}

export const SpeechProfileView = () => {
  const klartextEventConfig = useContext(KlartextEventConfigContext);

  const [numericKpis, setNumericKpis] = useState<{ [key: string]: number | [number, number] }>({
    TransparencyScore: [0, 0], // [Democrat, Republican]
    GlobalScore: [0, 0], // [Democrat, Republican]
    InclusivityScore: [0, 0], // [Democrat, Republican]
    RationalScore: [0, 0], // [Democrat, Republican]
    HopefulScore: [0, 0], // [Democrat, Republican]
  });

  const { data: transparencyScoreRepublicanAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/TransparencyScoreRepublican/values/average`,
  );

  const { data: transparencyScoreDemocratAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/TransparencyScoreDemocrat/values/average`,
  );

  const { data: globalScoreRepublicanAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/GlobalScoreRepublican/values/average`,
  );

  const { data: globalScoreDemocratAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/GlobalScoreDemocrat/values/average`,
  );

  const { data: inclusivityScoreRepublicanAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/InclusivityScoreRepublican/values/average`,
  );

  const { data: inclusivityScoreDemocratAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/InclusivityScoreDemocrat/values/average`,
  );

  const { data: rationalScoreRepublicanAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/RationalScoreRepublican/values/average`,
  );

  const { data: rationalScoreDemocratAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/RationalScoreDemocrat/values/average`,
  );

  const { data: hopefulScoreRepublicanAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/HopefulScoreRepublican/values/average`,
  );

  const { data: hopefulScoreDemocratAverage } = useDocument<NumericKpiPoint>(
    `events/${klartextEventConfig?.eventId}/numericKpis/HopefulScoreDemocrat/values/average`,
  );

  useEffect(() => {
    setNumericKpis({
      TransparencyScore: [transparencyScoreDemocratAverage?.value ?? 0, transparencyScoreRepublicanAverage?.value ?? 0],
      GlobalScore: [globalScoreDemocratAverage?.value ?? 0, globalScoreRepublicanAverage?.value ?? 0],
      InclusivityScore: [inclusivityScoreDemocratAverage?.value ?? 0, inclusivityScoreRepublicanAverage?.value ?? 0],
      RationalScore: [rationalScoreDemocratAverage?.value ?? 0, rationalScoreRepublicanAverage?.value ?? 0],
      HopefulScore: [hopefulScoreDemocratAverage?.value ?? 0, hopefulScoreRepublicanAverage?.value ?? 0],
    });
  }, [
    transparencyScoreRepublicanAverage,
    transparencyScoreDemocratAverage,
    globalScoreRepublicanAverage,
    globalScoreDemocratAverage,
    inclusivityScoreRepublicanAverage,
    inclusivityScoreDemocratAverage,
    rationalScoreRepublicanAverage,
    rationalScoreDemocratAverage,
    hopefulScoreRepublicanAverage,
    hopefulScoreDemocratAverage,
  ]);

  return (
    <div className="mt-7 space-y-7">
      {Object.entries(numericKpis).map(([kpi, value]) => {
        const isGlobalScore = kpi === 'GlobalScore' && isGlobalScoreAverage(value);
        const isTransparencyScore = kpi === 'TransparencyScore' && isTransparencyScoreAverage(value);
        const isInclusivityScore = kpi === 'InclusivityScore' && isInclusivityScoreAverage(value);
        const isRationalScore = kpi === 'RationalScore' && isRationalScoreAverage(value);
        const isHopefulScore = kpi === 'HopefulScore' && isHopefulScoreAverage(value);

        return (
          <div key={kpi} className="space-y-2 px-2">
            <div className="flex justify-between">
              <label className="font-medium">{getScaleLabels(kpi).min}</label>
              <label className="font-medium">{getScaleLabels(kpi).max}</label>
            </div>
            <div className="relative flex h-4 items-center justify-between rounded-3xl bg-eidraWhite px-2 py-2">
              <span className="inline-block h-1.5 w-0.5 rounded-lg bg-eidraDarkGrey"></span>
              <span className="inline-block h-1.5 w-0.5 rounded-lg bg-eidraDarkGrey"></span>
              <span className="inline-block h-1.5 w-0.5 rounded-lg bg-eidraDarkGrey"></span>
              {isGlobalScore && (
                <>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-democrat opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[0]) * 100}%`,
                    }}
                    title="Democrat"></span>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-republican opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[1]) * 100}%`,
                    }}
                    title="Republican"></span>
                </>
              )}
              {isTransparencyScore && (
                <>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-democrat opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[0]) * 100}%`,
                    }}
                    title="Democrat"></span>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-republican opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[1]) * 100}%`,
                    }}
                    title="Republican"></span>
                </>
              )}
              {isInclusivityScore && (
                <>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-democrat opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[0]) * 100}%`,
                    }}
                    title="Democrat"></span>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-republican opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[1]) * 100}%`,
                    }}
                    title="Republican"></span>
                </>
              )}
              {isRationalScore && (
                <>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-democrat opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[0]) * 100}%`,
                    }}
                    title="Democrat"></span>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-republican opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[1]) * 100}%`,
                    }}
                    title="Republican"></span>
                </>
              )}
              {isHopefulScore && (
                <>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-democrat opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[0]) * 100}%`,
                    }}
                    title="Democrat"></span>
                  <span
                    className="absolute block aspect-square h-6 -translate-x-1/2 rounded-full bg-republican opacity-80 shadow-sm transition-all duration-500"
                    style={{
                      left: `${getNormalizedValue(value[1]) * 100}%`,
                    }}
                    title="Republican"></span>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

function getScaleLabels(kpiName: string) {
  switch (kpiName) {
    case 'TransparencyScore':
      return { min: 'Ambiguous', max: 'Transparent' }; // Ambiguous, Transparent
    case 'GlobalScore':
      return { min: 'Local', max: 'Global' }; // Local, Global
    case 'InclusivityScore':
      return { min: 'Excluding', max: 'Including' }; // Exclusive, Inclusive
    case 'RationalScore':
      return { min: 'Emotional', max: 'Rational' }; // Emotional, Rational
    case 'HopefulScore':
      return { min: 'Dystopian', max: 'Hopeful' }; // Dystopian, Hopeful
    default:
      return { min: '', max: '' };
  }
}

/**
 * Normalize a value between the min and max values of the KPI scale (-10 / 10).
 * @param value - the value of the KPI
 * @param min - the minimum value of the KPI scale
 * @param max - the maximum value of the KPI scale
 */
function getNormalizedValue(value: number, min = -10, max = 10) {
  return (value - min) / (max - min);
}
